import { DownloadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Checkbox, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { DashboardName, downloadReason } from 'services/api/dashboard';
import { DashboardFilterType } from '../Filter/useFilter';
import {
  momentDateRange2FetchParams,
  platformAll2Undefined,
  storeType2FetchParams,
} from '../../utils';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useDashboardCurrency } from '../../hooks/useCurrency';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DepartmentsSelect } from '../Filter/DepartmentSelect';
import { DashboardPlatformSelectorFeature, DashboardStoreSelectorFeature } from 'utils/featureFlag';
import {
  mercadoNewDataDate,
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from '../Filter/Provider';
import { RegionFixedExchangeRateValue } from 'services/config/region';
import { Scene } from 'services/api/dashboard';
import dayjs, { Dayjs } from 'lib/dayjs';
import { DatePicker } from 'components/DatePicker';

export interface ExportDataProps {
  selectedReason: string[];
  scene: Scene;
  dashboardName: DashboardName;
  initialValue: DashboardFilterType;
}

function ExportData(props: ExportDataProps & { currency: RegionFixedExchangeRateValue }) {
  const { currency, scene, dashboardName, initialValue, selectedReason } = props;

  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();

  const [filter, setFilter] = useState({ ...initialValue, currency });

  const [visible, setVisible] = useState(false);

  const [checked, setChecked] = useState<string[]>([]);

  const { run: onDownload, loading: exporting } = useExportFile();

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments: filter.departments,
    scene,
    dashboardName,
    showPlatformAll: false,
    value: filter.platform,
    onChange: (value) => {
      setFilter((pre) => {
        const dateRange = pre.dateRange;
        const isMercadoFlag =
          dashboardName === 'OrderLoss' &&
          value === 'MERCADO' &&
          dateRange &&
          dateRange[0].isBefore(mercadoNewDataDate, 'days') &&
          dateRange[1].isSameOrAfter(mercadoNewDataDate, 'days');

        return {
          ...pre,
          platform: value,
          storeInfo: {},
          dateRange: isMercadoFlag ? [dayjs(mercadoNewDataDate), dateRange[1]] : dateRange,
        };
      });
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: filter.storeType,
    onChange: (storeType) => {
      setFilter((pre) => ({
        ...pre,
        storeType,
        storeInfo: {},
      }));
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform: filter.platform,
    departments: filter.departments,
    platformOptions,
    storeType: filter.storeType,
    storeInfo: filter.storeInfo,
    onChange: (storeInfo) =>
      setFilter((pre) => ({
        ...pre,
        storeInfo,
      })),
    scene,
  });

  const modal = (
    <Modal
      visible={visible}
      width={720}
      title={
        <div>
          {t('ViewDashboard.导出弃单明细')}
          <span className="text-xs text-[var(--fett-text-tertiary)] font-normal ml-2">
            {t('导出数据提示')}
          </span>
        </div>
      }
      onCancel={() => setVisible(false)}
      footer={[
        <span style={{ marginRight: '12px', color: 'var(--fett-text-tertiary)' }}>
          {t('aide.已选x项', { x: checked.length })}
        </span>,
        <Button onClick={() => setVisible(false)}>{t('translation.取消')}</Button>,
        <Button
          type="primary"
          disabled={checked.length === 0 || exporting}
          loading={exporting}
          onClick={async () => {
            setVisible(false);
            const {
              platform,
              dateRange,
              storeType,
              storeInfo: { list = [] },
              departments,
            } = filter;
            const params = {
              ...momentDateRange2FetchParams(dateRange),
              platform: platformAll2Undefined(platform) as General.PlatformUpperCase,
              storeType: storeType2FetchParams(storeType),
              storeIds: list.map((x) => x.id),
              departmentIds: departments?.map((_) => _.id),
              orderLossReasons: selectedReason,
              language: systemLanguage,
            } as any;
            if (checked.includes('COD')) {
              onDownload(() =>
                downloadReason({
                  ...params,
                  isCod: 1,
                }),
              );
            }
            if (checked.includes('NON_COD')) {
              onDownload(() =>
                downloadReason({
                  ...params,
                  isCod: 0,
                }),
              );
            }
          }}
        >
          {t('review.导出')}
        </Button>,
      ]}
    >
      <Content>
        <header className="flex gap-4">
          <DepartmentsSelect
            dashboardType="order"
            value={filter.departments}
            onChange={(departments) => {
              setFilter((pre) => ({
                ...pre,
                departments,
                platform: undefined,
                storeInfo: {},
              }));
            }}
          />
          {DashboardPlatformSelectorFeature && platformSelect}
          {DashboardStoreSelectorFeature && storeTypeSelect}
          {storeInfoSelect}
          {/* <Select
            value={filter.currency.region}
            options={currencyOptions}
            onChange={(value) => {
              const currency = currencyOptions.find((_) => _.region === value);
              setFilter((pre) => ({
                ...pre,
                currency: currency!,
              }));
            }}
          /> */}
        </header>
        <section className="flex gap-4 items-center">
          <span>{t('ViewDashboard.日期')}</span>
          <DatePicker.RangePicker
            className="w-[380px]"
            disabledDate={(current) => {
              return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
            }}
            value={filter.dateRange}
            onChange={(dateRange) => {
              setFilter((pre) => ({ ...pre, dateRange: dateRange as [Dayjs, Dayjs] }));
            }}
          />
        </section>
      </Content>
      <Content className="mt-4">
        <header>
          <Checkbox
            checked={checked.length === 2}
            indeterminate={checked.length > 0 && checked.length < 2}
            onChange={({ target: { checked } }) => setChecked(checked ? ['COD', 'NON_COD'] : [])}
          >
            {t('弃单明细')}
          </Checkbox>
        </header>
        <section>
          <Checkbox.Group
            value={checked}
            onChange={(value) => {
              setChecked(value as string[]);
            }}
          >
            <Checkbox value="COD">{t('COD订单')}</Checkbox>
            <Checkbox value="NON_COD">{t('非COD订单')}</Checkbox>
          </Checkbox.Group>
        </section>
      </Content>
    </Modal>
  );

  useEffect(() => {
    if (visible) {
      setFilter({
        ...initialValue,
        currency,
      });
    }
  }, [currency, initialValue, visible]);

  return (
    <>
      <Button type="ghost" icon={<DownloadOutlined />} onClick={() => setVisible(true)}>
        {t('ViewDashboard.导出弃单明细')}
      </Button>
      {modal}
    </>
  );
}

const Content = styled.div`
  border: solid 1px var(--fett-border-default);
  border-radius: 2px;
  > header {
    display: flex;
    align-items: center;
    height: 46px;
    padding: 0 16px;
    border-bottom: solid 1px var(--fett-border-default);
  }
  > section {
    padding: 16px 24px;
    > div {
      display: flex;
      & + div {
        margin-top: 12px;
      }
      > * {
        flex: 1 0 0;
      }
    }
  }
`;

export default function ExportDataWrapper(props: ExportDataProps) {
  const { currency } = useDashboardCurrency();

  if (!currency) {
    return null;
  }

  return <ExportData {...props} currency={currency} />;
}
